import HeaderAppBar from "./appbar";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FileText, Calendar, CheckCircle, XCircle, Package, Search } from "lucide-react";


interface OrderData {
  order_id: number | string;
  name: string;
  recieved_on: string; // ISO or date string from backend
  status: boolean;
}

interface ShowAllOrdersProps {
  ordersData: OrderData[];
}

function ShowAllOrders({ ordersData }: ShowAllOrdersProps) {
  const navigate = useNavigate();

  if (!ordersData || ordersData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mb-4">
          <Search className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-gray-900 text-xl font-bold mb-2">No Orders Found</h3>
        <p className="text-gray-500 text-center">
          No orders match the current criteria
        </p>
      </div>
    );
  }

  const reRouteToOrderDetailsPage = (order_id: OrderData["order_id"]) => {
    navigate(`/order-details/${order_id}`);
  };

  return (
    <div className="max-w-6xl mx-auto px-4">
      {/* Header Section */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
            <Package className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
            Orders List
          </h2>
        </div>
        <div className="h-1 w-24 bg-gradient-to-r from-orange-500 to-orange-600 mx-auto rounded-full mb-2"></div>
        <p className="text-gray-600 text-lg font-medium">
          Total {ordersData.length} order{ordersData.length !== 1 ? 's' : ''} found
        </p>
      </div>

      {/* Orders Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {ordersData.map((order) => (
          <div
            key={order.order_id}
            onClick={() => reRouteToOrderDetailsPage(order.order_id)}
            className="group cursor-pointer"
          >
            <div className="bg-white border border-gray-100 hover:border-orange-500/40 rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-orange-500 rounded-l-2xl"></div>
              {/* Header with Order ID and Status */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-orange-500/20 rounded-xl flex items-center justify-center">
                    <FileText className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="text-gray-900 text-lg font-bold group-hover:text-orange-500 transition-colors duration-300">
                      Bill #{order.order_id}
                    </h3>
                  </div>
                </div>
                
                {/* Status Badge */}
                <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${
                  order.status 
                    ? 'bg-green-50 text-green-700 border border-green-200' 
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {order.status ? (
                    <>
                      <CheckCircle className="w-3 h-3" />
                      Delivered
                    </>
                  ) : (
                    <>
                      <XCircle className="w-3 h-3" />
                      Pending
                    </>
                  )}
                </div>
              </div>

              {/* Customer Name */}
              <div className="mb-4">
                <div className="bg-gray-50/50 rounded-xl p-4 border border-gray-100">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-2 h-2 bg-orange-500 rounded-full shadow-[0_0_8px_rgba(249,115,22,0.4)]"></div>
                    <p className="text-gray-500 text-xs font-bold uppercase tracking-wider">Customer Name</p>
                  </div>
                  <p className="text-gray-900 text-lg font-bold">{order.name}</p>
                </div>
              </div>

              {/* Order Date */}
              <div className="flex items-center gap-2 text-gray-500 font-medium">
                <Calendar className="w-4 h-4" />
                <span className="text-sm">
                  {new Date(order.recieved_on).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>

              {/* Click Indicator */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-gray-500 text-xs text-center group-hover:text-orange-400 transition-colors duration-300">
                  Click to view details →
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

     
    </div>
  );
}

function ShowOrders() {
  const [ordersData, setOrdersData] = useState<OrderData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const location = useLocation();

  useEffect(() => {
    const loadOrdersData = () => {
      setIsLoading(true);
      const storedOrders = localStorage.getItem("all_orders");
      if (storedOrders) {
        try {
          const parsed = JSON.parse(storedOrders) as unknown;
          if (Array.isArray(parsed)) {
            // Basic runtime shape check
            const typed = parsed.filter(
              (o): o is OrderData =>
                o &&
                (typeof o.order_id === "number" || typeof o.order_id === "string") &&
                typeof o.name === "string" &&
                typeof o.recieved_on === "string" &&
                typeof o.status === "boolean"
            );
            setOrdersData(typed);
          } else {
            setOrdersData([]);
          }
        } catch {
          setOrdersData([]);
        }
      } else {
        setOrdersData([]);
      }
      setIsLoading(false);
    };

    loadOrdersData();
  }, [location.key]); // reload when navigation changes

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <div
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 28 28' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23000000'%3E%3Ccircle cx='1' cy='1' r='1'/%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />
      
      <HeaderAppBar />
      
      {/* Content Section */}
      <div className="px-4 py-8 relative">
        {isLoading ? (
          <div className="flex flex-col justify-center items-center py-20 gap-4">
            <div className="relative">
              <div className="w-20 h-20 border-4 border-orange-500/30 border-t-orange-500 rounded-full animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-10 h-10 bg-orange-500 rounded-full animate-pulse"></div>
              </div>
            </div>
            <p className="text-gray-900 text-lg font-bold">Loading orders...</p>
          </div>
        ) : (
          <ShowAllOrders ordersData={ordersData} />
        )}
      </div>
    </div>
  );
}

export default ShowOrders;
